import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { Box, Checkbox, DialogContent, FormControlLabel, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
	addNewUserRequest,
	createDashboardUserRequest,
	needConfirmAccessCreateDashboardUser,
	updateUserRequest,
} from 'redux/reducers/settings/reducer';
import {
	getCreateDashboardUser,
	getCreateDashboardUserError,
	getCreateDashboardUserLoader,
	getNeedConfirmAccessCreateDashboardUser,
} from 'redux/reducers/settings/selectors';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { TEditUserDialogProps } from './types';

export const EditUserDialog: FC<TEditUserDialogProps> = ({ open, setClose, data, type }) => {
	const dispatch = useDispatch();
	const needConfirmAccess = useSelector(getNeedConfirmAccessCreateDashboardUser);
	const createDashboardUser = useSelector(getCreateDashboardUser);
	const createDashboardUserLoader = useSelector(getCreateDashboardUserLoader);
	const createDashboardUserError = useSelector(getCreateDashboardUserError);

	const [firstName, setFirstName] = useState<string | null>(data?.firstName ?? null);
	const [lastName, setLastName] = useState<string | null>(data?.lastName ?? null);
	const [email, setEmail] = useState<string | null>(data?.email ?? null);
	const [onlyForNotification, setOnlyForNotification] = useState<boolean>(false);
	const [confirmCreateDialogOpen, setConfirmCreateDialogOpen] = useState<boolean>(false);
	const [createSubmitted, setCreateSubmitted] = useState<boolean>(false);
	const eraseData = () => {
		setFirstName(null);
		setLastName(null);
		setEmail(null);
		setOnlyForNotification(false);
		setConfirmCreateDialogOpen(false);
		setCreateSubmitted(false);
		dispatch(needConfirmAccessCreateDashboardUser(false));
	};

	useEffect(() => {
		setFirstName(data?.firstName ?? null);
		setLastName(data?.lastName ?? null);
		setEmail(data?.email ?? null);
	}, [data, open]);

	useEffect(() => {
		if (!open) {
			setConfirmCreateDialogOpen(false);
			setCreateSubmitted(false);
			return;
		}
		// сбрасываем флаг, чтобы не показывать подтверждение из прошлой попытки
		dispatch(needConfirmAccessCreateDashboardUser(false));
	}, [dispatch, open]);

	useEffect(() => {
		// В `UserManagement` компонент рендерится минимум в двух экземплярах (add/edit),
		// поэтому открывать подтверждение нужно только для активного (open) диалога добавления.
		if (!open) return;
		if (type !== 'add') return;
		if (onlyForNotification) return;
		if (needConfirmAccess) setConfirmCreateDialogOpen(true);
	}, [needConfirmAccess, onlyForNotification, open, type]);

	useEffect(() => {
		// для сценария: создать dashboard user без необходимости подтверждения
		if (!open) return;
		if (type !== 'add') return;
		if (onlyForNotification) return;
		if (!createSubmitted) return;
		if (createDashboardUserLoader) return;
		if (needConfirmAccess) return;
		if (createDashboardUserError) return;
		if (createDashboardUser) setClose();
	}, [
		createDashboardUser,
		createDashboardUserError,
		createDashboardUserLoader,
		createSubmitted,
		needConfirmAccess,
		onlyForNotification,
		open,
		setClose,
		type,
	]);

	const handleEdit = () => {
		if (type === 'add') {
			const newData = {
				firstName: firstName ?? data?.firstName ?? '',
				lastName: lastName ?? data?.lastName ?? '',
				email: email ?? data?.email ?? '',
				confirmAccess: false,
				notificationOnly: onlyForNotification,
			};
			dispatch(createDashboardUserRequest(newData));
			eraseData();
			setClose();
		} else if (type === 'edit') {
			if (!data) return;
			const newData = {
				...data,
				firstName: firstName ?? data?.firstName ?? '',
				lastName: lastName ?? data?.lastName ?? '',
				email: email ?? data?.email ?? '',
			};
			dispatch(updateUserRequest(newData));
			eraseData();
			setClose();
		}
	};

	const validateEmail = (txt: string) => {
		const pattern = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
		return pattern.test(txt);
	};

	const handleConfirmCreateDashboardUser = () => {
		const newData = {
			firstName: firstName ?? data?.firstName ?? '',
			lastName: lastName ?? data?.lastName ?? '',
			email: email ?? data?.email ?? '',
			confirmAccess: true,
			notificationOnly: onlyForNotification,
		};
		dispatch(createDashboardUserRequest(newData));
		eraseData();
		setConfirmCreateDialogOpen(false);
		setClose();
	};

	const handleCancelConfirmCreateDashboardUser = () => {
		setConfirmCreateDialogOpen(false);
		// dispatch(needConfirmAccessCreateDashboardUser(false));
	};
	const handleCancel = () => {
		eraseData();
		setClose();
	};

	return (
		<>
			<Dialog
				open={open}
				// onClose={setClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					{type === 'edit' ? 'Edit personal data' : 'Add new user'}
				</DialogTitle>
				<DialogContent>
					<Box
						component="form"
						autoComplete="off"
						sx={{
							'& .MuiTextField-root': { m: 1 },
						}}
					>
						<TextField
							sx={{
								'& .MuiInputBase-root': {
									borderRadius: '12px!important',
									mb: '10px',
								},
							}}
							label="First name"
							value={firstName ?? ''}
							// error={!firstName}
							onChange={(e) => setFirstName(e.target.value)}
							fullWidth
							required
						/>
						<TextField
							sx={{
								'& .MuiInputBase-root': {
									borderRadius: '12px!important',
									mb: '10px',
								},
							}}
							label="Last name"
							value={lastName ?? ''}
							// error={!lastName}
							onChange={(e) => setLastName(e.target.value)}
							fullWidth
							required
						/>
						<TextField
							sx={{
								'& .MuiInputBase-root': {
									borderRadius: '12px!important',
								},
							}}
							label="Email"
							value={email ?? ''}
							type="email"
							error={!!email && !validateEmail(email ?? '')}
							onChange={(e) => setEmail(e.target.value)}
							fullWidth
							required
						/>

						{type === 'add' && (
							<FormControlLabel
								sx={{
									'& .MuiFormControlLabel-label': {
										fontSize: '1rem',
										color: 'text.secondary',
									},
									mt: '10px',
									ml: '0',
								}}
								control={
									<Checkbox
										checked={onlyForNotification}
										onChange={(e) => setOnlyForNotification(e.target.checked)}
										aria-label="Notifications only (no dashboard access)"
									/>
								}
								label="Notifications only (no dashboard access)"
							/>
						)}

						{/* {type === 'add' && (
							<FormControlLabel
								sx={{
									'& .MuiFormControlLabel-label': {
										fontSize: '1rem',
										color: 'text.secondary',
									},
									mt: '10px',
									ml: '0',
								}}
								control={
									<Checkbox
										checked={confirmAccess}
										onChange={(e) => setConfirmAccess(e.target.checked)}
										aria-label="Confirm access"
									/>
								}
								label="Confirm access"
							/>
						)} */}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancel}>Cancel</Button>
					<Button
						onClick={handleEdit}
						autoFocus
						disabled={
							!firstName ||
							!lastName ||
							!validateEmail(email ?? '') ||
							(createDashboardUserLoader && type === 'add' && !onlyForNotification)
						}
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			{open && type === 'add' && !onlyForNotification && (
				<ConfirmationDialog
					open={confirmCreateDialogOpen}
					setClose={handleCancelConfirmCreateDashboardUser}
					handleConfirm={handleConfirmCreateDashboardUser}
					title="User already exists"
					text="This email already exists. Do you want to confirm granting dashboard access to this user?"
					confirmButtonText="Confirm"
					cancelButtonText="Cancel"
				/>
			)}
		</>
	);
};
